/* GASSISA
=========================================================================================
* Descripción: Dashboard central con planeta neuronal y accesos rápidos a los módulos
                principales del sistema.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 10/10/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 10/10/2026 | Lic. José Luis Aragón Cervantes  | Círculo de íconos centrado en el video
* ---------------------------------------------------------------------------------------
*/
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useSesion } from "../../../context/System/SesionContext";
import { useLayout } from "../../../context/System/LayoutContext";
import { useConfiguracion } from "../../../context/System/ConfiguracionContext";
import { dashboardQuickAccess } from "../../../settings/menuModel";

import planetaVideo from "../../../img/planeta-neuronal-realista.mp4";
import planetaPoster from "../../../img/planeta-neuronal-realista-poster.jpg";

/* Normaliza para comparar nombres de página entre tenants sin depender de acentos ni mayúsculas */
const normalizar = (texto) =>
    (texto ?? "")
        .toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

/* --- Geometría del video (medida sobre el fotograma) ------------------------------------ */
const ASPECT_VIDEO = 16 / 9;        // 640x360
const RADIO_PLANETA_Y = 0.41;        // radio del planeta (anillos verdes) respecto al alto del cuadro
const ESCALA_VIDEO = 0.47;           // 40% menos un 7,5% acumulado: lado de la caja del cuadro, como fracción del contenedor
const AMPLIACION_VIDEO = 1.1;        // el cuadro se sale un 10% de la caja para tapar la costura del borde
const DESPLAZAMIENTO_VIDEO = 7.6;    // px; debe coincidir con --planeta-desplazamiento-video del SCSS

/* --- Círculo de íconos (PORTÁTIL: 30% más compacto que desktop) ------------------------- */
const RADIO_BASE = 0.21;           
/* +2 cm al radio y +3 cm al DIÁMETRO (1,5 cm de radio) = +3,5 cm, más +2 cm de diámetro (1 cm de radio) */
const AMPLIAR_RADIO = 70;
const DESPLAZAMIENTO_Y = -50;     
const SEPARACION_PLANETA = 8;       
const ALTO_ETIQUETA = 31;           

const BOTON_MINIMO = 56;
const BOTON_MAXIMO = 96;
const BOTON_VW = 0.056;

const estimarMedioBoton = () =>
    Math.min(Math.max(window.innerWidth * BOTON_VW, BOTON_MINIMO), BOTON_MAXIMO) / 2;

const SizeSoftXDashboardHome = () => {
    const { definicionMenu } = useSesion();
    const { handleCloseMenuClient } = useLayout();
    const { theme } = useConfiguracion();
    const videoRef = useRef(null);
    const contenedorRef = useRef(null);
    const [geometria, setGeometria] = useState(null);

    /* Color primario de las herramientas, tomado del tema (nunca hardcodeado) */
    const colorPrimario = theme?.palette?.primary?.main ?? "#0062AE";

    /* Misma lógica de visibilidad del menú lateral:
       SizeSoftXMenu.jsx (grupo con mostrar) + SizeSoftXMenuGroup.jsx (página con mostrar y no bloqueada) */
    const accesos = useMemo(() => {
        const disponibles = {};

        Object.keys(definicionMenu ?? {})
            .filter((clave_modulo) => clave_modulo !== "_paths" && definicionMenu[clave_modulo]?.mostrar)
            .forEach((clave_modulo) => {
                const paginas = definicionMenu[clave_modulo]?._paginas ?? {};
                Object.keys(paginas).forEach((clave_pagina) => {
                    const pagina = paginas[clave_pagina];
                    if (!pagina?.mostrar || pagina?.status) return;
                    disponibles[normalizar(clave_pagina)] = {
                        nombre: pagina.nombre ?? clave_pagina,
                        path: pagina.path,
                        grupo: definicionMenu[clave_modulo]?.nombre ?? clave_modulo,
                        hotkey: pagina.hotkey ?? "",
                    };
                });
            });

        return dashboardQuickAccess
            .map((item) => {
                const pagina = disponibles[normalizar(item.pagina)];
                return pagina ? { ...item, ...pagina } : null;
            })
            .filter(Boolean);
    }, [definicionMenu]);

    /* Ángulos reales de cada acceso. Con número impar uno cae a las 6:00 pero ninguno
       a las 12:00, así que arriba hay más aire que abajo: sin esto se reserva espacio de más
       y el círculo queda más pequeño de lo necesario. */
    const alcances = useMemo(() => {
        const total = accesos.length;
        if (!total) return { sinMax: 1, sinMin: -1 };

        const paso = 360 / total;
        const inicio = -90 - paso / 2;
        let sinMax = -Infinity;
        let sinMin = Infinity;

        for (let indice = 0; indice < total; indice += 1) {
            const seno = Math.sin(((inicio + indice * paso) * Math.PI) / 180);
            if (seno > sinMax) sinMax = seno;
            if (seno < sinMin) sinMin = seno;
        }

        return { sinMax, sinMin };
    }, [accesos.length]);

    /* El círculo se traza sobre el centro del video */
    const medir = useCallback(() => {
        const contenedor = contenedorRef.current;
        if (!contenedor) return;

        const base = contenedor.getBoundingClientRect();
        if (!base.width || !base.height) return;

        /* Tamaño del <video>: se calcula desde el CONTENEDOR, nunca desde la caja del propio
           video. Medir el video para luego redimensionarlo realimenta la medición y produce
           fluctuaciones de un pixel (la "raya" que aparecía al redimensionar la ventana).

           Se reproduce exactamente lo que se veía antes —el cuadro 16:9 encajado con contain
           dentro de una caja del 40% x 40% del contenedor— pero el elemento recibe YA el
           tamaño final del cuadro, no una caja cuadrada con franjas. Como el elemento queda
           en la proporción real, object-fit no tiene nada que recortar y no sobran bandas:
           el <video> se compone en una capa acelerada por GPU que no respeta su
           background-color, así que cualquier banda sobrante se vería negra. */
        const cajaAncho = base.width * ESCALA_VIDEO;
        const cajaAlto = base.height * ESCALA_VIDEO;

        let contenidoAncho = cajaAncho;
        let contenidoAlto = cajaAlto;
        if (cajaAncho / cajaAlto > ASPECT_VIDEO) {
            contenidoAncho = cajaAlto * ASPECT_VIDEO;
        } else {
            contenidoAlto = cajaAncho / ASPECT_VIDEO;
        }

        /* Ampliación del 10% y ajuste a píxeles enteros.
           El cuadro se sale de su caja: aunque el compositor dibuje el video un pelo más
           grande, el blanco de la caja lo absorbe y no aparece la raya. Con object-fit: cover
           ese 10% se come por los cuatro lados, sin deformar.
           Ojo: al crecer, el video invade el círculo de íconos; el radio ya se deriva del alto
           del cuadro (radioPlaneta), así que el círculo se abre solo para no encimarse.
           Los píxeles enteros evitan la costura clásica del video acelerado por GPU: con un
           ancho fraccionario (p. ej. 440.70 px) el borde cae a medio píxel y al hacer zoom la
           caja baila entre valores, y la costura aparece y desaparece. Con ancho y alto
           enteros el borde siempre cae entre dos píxeles. */
        const dpr = window.devicePixelRatio || 1;
        const alinearPixel = (valor) => Math.round(valor * dpr) / dpr;
        contenidoAncho = alinearPixel(contenidoAncho * AMPLIACION_VIDEO);
        contenidoAlto = alinearPixel(contenidoAlto * AMPLIACION_VIDEO);

        /* El centro se deriva del contenedor con el mismo desplazamiento del CSS
           (translate(-50%, -50%) desde left/top al 50%), no de la caja del video. */
        const centroX = base.width / 2;
        const centroY = base.height / 2 + DESPLAZAMIENTO_VIDEO;
        const radioPlaneta = RADIO_PLANETA_Y * contenidoAlto;

        const boton = contenedor.querySelector(".dashboard-planeta__boton");
        const enlace = contenedor.querySelector(".dashboard-planeta__enlace");
        const medioBoton = boton ? boton.offsetWidth / 2 : estimarMedioBoton();
        /* Espacio real que ocupan la etiqueta y el atajo debajo del ícono. Se mide y no se supone:
           "Traslados A o H" y "Centro de Costos" ocupan distinto y el texto escala con la
           pantalla, así que un valor fijo dejaba fuera el ícono de abajo. */
        const bajoBoton = enlace
            ? Math.max(enlace.offsetHeight - medioBoton, ALTO_ETIQUETA)
            : ALTO_ETIQUETA;

        /* Manda el RADIO pedido (base + AMPLIAR_RADIO), acotado por lo que cabe
           usando los ángulos reales: 2·radio·(sinMax − sinMin) + 2·medioIcono + etiqueta = alto. */
        const total = Math.max(accesos.length, 1);
        const radioPedido = Math.min(base.width, base.height) * RADIO_BASE + AMPLIAR_RADIO;
        const radioSinEncimado = medioBoton / Math.sin(Math.PI / total);
        const radioPorAire = radioPlaneta + medioBoton + SEPARACION_PLANETA;
        const alcanceVertical = Math.max(alcances.sinMax - alcances.sinMin, 0.001);
        const radioMaximo =
            (base.height - 2 * medioBoton - bajoBoton) / alcanceVertical;

        const radio = Math.max(
            Math.max(radioSinEncimado, radioPorAire),
            Math.min(radioPedido, radioMaximo)
        );

        /* El centro se mantiene donde se pidió y sólo se ajusta lo justo para que quepa */
        const centroYDeseado = centroY + DESPLAZAMIENTO_Y;
        const centroYAjustado = Math.min(
            Math.max(centroYDeseado, -alcances.sinMin * radio + medioBoton),
            base.height - alcances.sinMax * radio - medioBoton - bajoBoton
        );

        setGeometria((anterior) =>
            anterior &&
                Math.abs(anterior.centroX - centroX) < 0.5 &&
                Math.abs(anterior.centroY - centroYAjustado) < 0.5 &&
                Math.abs(anterior.radio - radio) < 0.5 &&
                Math.abs(anterior.videoAncho - contenidoAncho) < 0.5 &&
                Math.abs(anterior.videoAlto - contenidoAlto) < 0.5
                ? anterior
                : {
                      centroX,
                      centroY: centroYAjustado,
                      radio,
                      videoAncho: contenidoAncho,
                      videoAlto: contenidoAlto,
                  }
        );
    }, [accesos.length, alcances]);

    useEffect(() => {
        const contenedor = contenedorRef.current;
        if (!contenedor) return undefined;

        medir();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", medir);
            return () => window.removeEventListener("resize", medir);
        }

        const observador = new ResizeObserver(medir);
        observador.observe(contenedor);
        return () => observador.disconnect();
    }, [medir]);

    /* Segunda pasada: ya con los íconos montados se mide su diámetro real */
    useEffect(() => {
        medir();
    }, [accesos, medir]);

    /* Posiciones fijas en sentido horario, sobre un círculo perfecto */
    const posiciones = useMemo(() => {
        if (!geometria || !accesos.length) return [];

        const paso = 360 / accesos.length;
        const inicio = -90 - paso / 2;

        return accesos.map((acceso, indice) => {
            const radianes = ((inicio + indice * paso) * Math.PI) / 180;
            return {
                ...acceso,
                izquierda: geometria.centroX + geometria.radio * Math.cos(radianes),
                arriba: geometria.centroY + geometria.radio * Math.sin(radianes),
            };
        });
    }, [accesos, geometria]);

    /* Evita que el video siga decodificando cuando la pestaña no está visible */
    useEffect(() => {
        const handleVisibilidad = () => {
            const video = videoRef.current;
            if (!video) return;
            if (document.hidden) {
                video.pause();
            } else {
                const reproduccion = video.play();
                if (reproduccion?.catch) reproduccion.catch(() => {});
            }
        };

        document.addEventListener("visibilitychange", handleVisibilidad);
        return () => document.removeEventListener("visibilitychange", handleVisibilidad);
    }, []);

    const handleClickAcceso = useCallback(() => {
        handleCloseMenuClient();
    }, [handleCloseMenuClient]);

    return (
        <div className="dashboard-planeta" ref={contenedorRef} style={{ "--planeta-color": colorPrimario }}>
            <video
                className="dashboard-planeta__video"
                src={planetaVideo}
                poster={planetaPoster}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                ref={videoRef}
                aria-hidden="true"
                /* Tamaño real del cuadro en px, medido sobre el contenedor. Hasta que haya
                   medición (primer render) manda el 40% x 40% del SCSS. Al quedar en 16:9
                   exacto, el elemento no tiene franjas y object-fit no recorta. */
                style={
                    geometria
                        ? { width: `${geometria.videoAncho}px`, height: `${geometria.videoAlto}px` }
                        : undefined
                }
            />

            <div
                className="dashboard-planeta__velo"
                aria-hidden="true"
                style={
                    geometria
                        ? { width: `${geometria.videoAncho}px`, height: `${geometria.videoAlto}px` }
                        : undefined
                }
            />

            <nav className="dashboard-planeta__orbita" aria-label="Accesos rápidos">
                {posiciones.map((acceso) => {
                    const Icono = acceso.Icon;
                    const helper = acceso.hotkey
                        ? `${acceso.nombre} · ${acceso.grupo} (Alt+${acceso.hotkey})`
                        : `${acceso.nombre} · ${acceso.grupo}`;

                    return (
                        <Tooltip key={`${acceso.grupo}-${acceso.nombre}`} title={helper} placement="top">
                            <Link
                                to={acceso.path}
                                className="dashboard-planeta__enlace"
                                style={{ left: `${acceso.izquierda}px`, top: `${acceso.arriba}px` }}
                                onClick={handleClickAcceso}
                                aria-label={acceso.nombre}
                            >
                                <span className="dashboard-planeta__boton">
                                    <Icono className="icon" />
                                </span>
                                <span className="dashboard-planeta__etiqueta">{acceso.nombre}</span>
                                {acceso.hotkey ? (
                                    <span className="dashboard-planeta__atajo">Alt+{acceso.hotkey}</span>
                                ) : null}
                            </Link>
                        </Tooltip>
                    );
                })}
            </nav>
        </div>
    );
};

export default SizeSoftXDashboardHome;
